// Predictive search fallback — only runs if Shrine CDN hasn't defined these elements
(function () {
  function debounce(fn, wait) {
    let timer;
    return function (...args) {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  // ── SearchForm ───────────────────────────────────────────────────────────────
  class SearchForm extends HTMLElement {
    constructor() {
      super();
      this.input = this.querySelector('input[type="search"]');
      this.resetButton = this.querySelector('button[type="reset"]');

      if (!this.input) return;

      this.input.form.addEventListener('reset', this.onFormReset.bind(this));
      this.input.addEventListener(
        'input',
        debounce(this.onChange.bind(this), 300)
      );
    }

    onChange() {
      this.toggleResetButton();
    }

    shouldResetForm() {
      return !document.querySelector('[aria-selected="true"]');
    }

    onFormReset(event) {
      if (!this.shouldResetForm()) {
        event.preventDefault();
        event.stopPropagation();
        this.input.value = '';
        this.input.focus();
      }
    }

    toggleResetButton() {
      if (!this.resetButton) return;
      const hidden = this.resetButton.classList.contains('hidden');
      if (this.input.value.length > 0 && hidden) {
        this.resetButton.classList.remove('hidden');
      } else if (this.input.value.length === 0 && !hidden) {
        this.resetButton.classList.add('hidden');
      }
    }
  }

  // ── PredictiveSearch ─────────────────────────────────────────────────────────
  class PredictiveSearch extends SearchForm {
    constructor() {
      super();
      this.cachedResults = {};
      this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
      this.isOpen = false;
      this.abortController = new AbortController();

      if (!this.input || !this.predictiveSearchResults) return;

      this.setupEventListeners();
    }

    setupEventListeners() {
      this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));
      this.input.addEventListener('focus', this.onInputFocus.bind(this));
      this.input.addEventListener('keyup', this.onKeyup.bind(this));
      this.input.addEventListener('keydown', this.onKeydown.bind(this));

      document.addEventListener('click', (e) => {
        if (!this.contains(e.target)) this.close(false);
      });
    }

    onChange() {
      super.onChange();
      const term = this.input.value.trim();
      if (!term.length) {
        this.close(true);
        return;
      }
      this.getSearchResults(term);
    }

    onInputFocus() {
      if (window.innerWidth < 750) {
        this.scrollIntoView({ behavior: 'smooth' });
      }
      if (this.input.value.trim().length > 0) {
        this.getSearchResults(this.input.value.trim());
      }
    }

    onFormSubmit(event) {
      // Let normal form submit happen unless an option is selected via keyboard
      const selected = this.querySelector('[aria-selected="true"] a');
      if (selected) {
        event.preventDefault();
        selected.click();
      }
    }

    onFormReset(event) {
      super.onFormReset(event);
      if (super.shouldResetForm()) {
        this.abortController.abort();
        this.abortController = new AbortController();
        this.close(true);
      }
    }

    onKeyup(event) {
      if (!this.input.value.trim().length) {
        this.close(true);
        return;
      }
      switch (event.code) {
        case 'ArrowUp':
          event.preventDefault();
          this.switchOption('up');
          break;
        case 'ArrowDown':
          event.preventDefault();
          this.switchOption('down');
          break;
        case 'Enter':
          this.selectOption();
          break;
        case 'Escape':
          this.close(false);
          break;
      }
    }

    onKeydown(event) {
      if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
        event.preventDefault();
      }
    }

    switchOption(direction) {
      if (!this.hasAttribute('open')) return;
      const moveUp = direction === 'up';
      const allOptions = Array.from(this.querySelectorAll('[role="option"]'));
      if (!allOptions.length) return;

      const currentIndex = allOptions.findIndex(
        (el) => el.getAttribute('aria-selected') === 'true'
      );

      let nextIndex;
      if (currentIndex === -1) {
        nextIndex = moveUp ? allOptions.length - 1 : 0;
      } else {
        allOptions[currentIndex].setAttribute('aria-selected', 'false');
        nextIndex = moveUp
          ? (currentIndex - 1 + allOptions.length) % allOptions.length
          : (currentIndex + 1) % allOptions.length;
      }

      allOptions[nextIndex].setAttribute('aria-selected', 'true');
      this.input.setAttribute('aria-activedescendant', allOptions[nextIndex].id || '');
    }

    selectOption() {
      const selected = this.querySelector('[aria-selected="true"] a, [aria-selected="true"] button');
      if (selected) selected.click();
    }

    getSearchResults(searchTerm) {
      const key = searchTerm.toLowerCase().replace(/\s+/g, '-');
      this.setLoadingState();

      if (this.cachedResults[key]) {
        this.renderSearchResults(this.cachedResults[key]);
        return;
      }

      const url =
        `${window.routes.predictive_search_url}` +
        `?q=${encodeURIComponent(searchTerm)}` +
        `&section_id=predictive-search` +
        `&resources[type]=product,query,collection` +
        `&resources[limit]=4` +
        `&resources[options][fields]=title,product_type,variants.title,vendor,tag`;

      fetch(url, { signal: this.abortController.signal })
        .then((response) => {
          if (!response.ok) throw new Error(response.status);
          return response.text();
        })
        .then((html) => {
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');
          const section = doc.querySelector('#shopify-section-predictive-search');
          const markup = section ? section.innerHTML : '';
          this.cachedResults[key] = markup;
          this.renderSearchResults(markup);
        })
        .catch((err) => {
          if (err.name === 'AbortError') return;
          this.removeAttribute('loading');
          throw err;
        });
    }

    setLoadingState() {
      this.setAttribute('loading', '');
      this.setAttribute('open', '');
      this.input.setAttribute('aria-expanded', 'true');
    }

    renderSearchResults(markup) {
      this.predictiveSearchResults.innerHTML = markup;
      this.removeAttribute('loading');
      this.setAttribute('open', '');
      this.input.setAttribute('aria-expanded', 'true');
      this.isOpen = true;
    }

    close(clearTerm = false) {
      this.abortController.abort();
      this.abortController = new AbortController();

      if (clearTerm) this.input.value = '';
      this.removeAttribute('open');
      this.removeAttribute('loading');
      this.input.setAttribute('aria-expanded', 'false');
      this.input.setAttribute('aria-activedescendant', '');
      this.isOpen = false;
    }
  }

  // Register only if CDN hasn't already defined them
  if (!customElements.get('search-form')) {
    customElements.define('search-form', SearchForm);
  }
  if (!customElements.get('predictive-search')) {
    customElements.define('predictive-search', PredictiveSearch);
  }
})();
